import { CartItem } from '../types/cart';

const CART_STORAGE_KEY = 'yugen_guest_cart';

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const normalizeNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.replace(',', '.').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const SHIPPING_FIELD_LABELS: Record<string, string> = {
  weight_grams: 'вес',
  package_length_cm: 'длина',
  package_width_cm: 'ширина',
  package_height_cm: 'высота',
};

const sortPriceTiers = (priceTiers: CartItem['priceTiers']) =>
  [...priceTiers]
    .map((tier) => ({
      minQuantity: Math.max(1, Number(tier.minQuantity || 1)),
      price: Number(tier.price || 0),
    }))
    .filter((tier) => Number.isFinite(tier.price) && tier.price >= 0)
    .sort((a, b) => a.minQuantity - b.minQuantity);

const resolveUnitPriceForQuantity = (item: CartItem, quantity: number): number | null => {
  if (item.priceTiers.length > 0) {
    const sortedTiers = sortPriceTiers(item.priceTiers);
    const matchedTier =
      [...sortedTiers].reverse().find((tier) => quantity >= tier.minQuantity) || sortedTiers[0];

    if (matchedTier) {
      return matchedTier.price;
    }
  }

  return normalizeNumber(item.unitPrice);
};

const recalculateCartItem = (item: CartItem, quantity: number): CartItem => {
  const nextQuantity = Math.max(0, quantity);
  const unitPrice = resolveUnitPriceForQuantity(item, nextQuantity);

  return {
    ...item,
    quantity: nextQuantity,
    unitPrice,
    lineTotal: unitPrice !== null ? Number((unitPrice * nextQuantity).toFixed(2)) : null,
  };
};

const sanitizeCartItem = (item: CartItem): CartItem => {
  const baseItem: CartItem = {
    key: String(item.key),
    productId: String(item.productId),
    name: item.name,
    sku: item.sku || '',
    image: item.image || '/images/placeholder.jpg',
    options: item.options || {},
    quantity: Math.max(1, Number(item.quantity || 1)),
    unitPrice: normalizeNumber(item.unitPrice),
    lineTotal: normalizeNumber(item.lineTotal),
    priceTiers: Array.isArray(item.priceTiers) ? item.priceTiers : [],
    itemSnapshot: item.itemSnapshot || {},
  };

  return recalculateCartItem(baseItem, baseItem.quantity);
};

export const getCart = (): CartItem[] => {
  if (!isBrowser()) {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map(sanitizeCartItem);
  } catch {
    return [];
  }
};

export const saveCart = (cart: CartItem[]) => {
  if (!isBrowser()) {
    return cart;
  }

  if (cart.length === 0) {
    window.localStorage.removeItem(CART_STORAGE_KEY);
    return cart;
  }

  window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  return cart;
};

export const addToCart = (item: CartItem, sourceCart: CartItem[] = getCart()) => {
  const nextCart = [...sourceCart];
  const existingIndex = nextCart.findIndex((cartItem) => cartItem.key === item.key);

  if (existingIndex === -1) {
    nextCart.push(recalculateCartItem(sanitizeCartItem(item), item.quantity || 1));
    return nextCart;
  }

  const existingItem = nextCart[existingIndex];
  nextCart[existingIndex] = recalculateCartItem(
    {
      ...existingItem,
      priceTiers: item.priceTiers.length > 0 ? item.priceTiers : existingItem.priceTiers,
      itemSnapshot: item.itemSnapshot || existingItem.itemSnapshot,
    },
    existingItem.quantity + Math.max(1, item.quantity || 1),
  );

  return nextCart;
};

export const removeFromCart = (itemKey: string, sourceCart: CartItem[] = getCart()) =>
  sourceCart.filter((item) => item.key !== itemKey);

export const updateQuantity = (itemKey: string, quantity: number, sourceCart: CartItem[] = getCart()) =>
  sourceCart
    .map((item) => (item.key === itemKey ? recalculateCartItem(item, quantity) : item))
    .filter((item) => item.quantity > 0);

export const clearCart = () => {
  if (isBrowser()) {
    window.localStorage.removeItem(CART_STORAGE_KEY);
  }
  return [];
};

export const getCartCount = (cart: CartItem[] = getCart()) =>
  cart.reduce((total, item) => total + item.quantity, 0);

export const getCartTotal = (cart: CartItem[] = getCart()) =>
  Number(
    cart
      .reduce((total, item) => total + (item.lineTotal || 0), 0)
      .toFixed(2),
  );

export const hasCartPricingIssues = (cart: CartItem[] = getCart()) =>
  cart.some((item) => item.unitPrice === null || item.lineTotal === null);

export const getCartShippingIssues = (cart: CartItem[] = getCart()) =>
  cart
    .map((item) => {
      const snapshot = item.itemSnapshot || {};
      const missingFields = Object.entries(SHIPPING_FIELD_LABELS)
        .filter(([key]) => {
          const value = normalizeNumber(snapshot[key as keyof typeof snapshot] as number | string | null | undefined);
          return value === null || value <= 0;
        })
        .map(([, label]) => label);

      if (!missingFields.length) {
        return null;
      }

      return {
        key: item.key,
        name: item.name,
        sku: item.sku,
        options: item.options,
        missingFields,
      };
    })
    .filter(
      (
        issue,
      ): issue is {
        key: string;
        name: string;
        sku: string;
        options: Record<string, string>;
        missingFields: string[];
      } => issue !== null,
    );

export const hasCartShippingIssues = (cart: CartItem[] = getCart()) =>
  getCartShippingIssues(cart).length > 0;
